import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentBikeParkingUseWithdrawalApplyLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatResidentBikeParkingUseWithdrawalApplyForm from './resident/NatResidentBikeParkingUseWithdrawalApplyForm'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {
    NatPutBikeParkingUseWithdrawalApplyRequest,
    NatPutBikeParkingUseWithdrawalApplyResponse,
} from '../repository/NatPutBikeParkingUseWithdrawalApply'
import {PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentBikeParkingUseWithdrawalApplyLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    withdrawalArea: Nullable<string>
    withdrawalDate: Nullable<string>
    bikeCarryingOutDate: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentBikeParkingUseWithdrawalApplyInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            withdrawalArea: null,
            withdrawalDate: null,
            bikeCarryingOutDate: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyId, condominiumCd, applicationRepo, eventEmitter} = this.props
        const applyInfo = this.props.location.state?.applyInfo

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultWithdrawalArea = applyInfo?.withdrawalArea ?? null
        let defaultWithdrawalDate = applyInfo?.withdrawalDate ?? null
        let defaultBikeCarryingOutDate = applyInfo?.bikeCarryingOutDate ?? null


        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const bikeParkingUsesWithdrawalDetail = await this.props.asyncCallFunction(
                    applicationRepo.getBikeParkingUseWithdrawalApplyForResident.bind(
                        applicationRepo, condominiumCd, applyId,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (bikeParkingUsesWithdrawalDetail && bikeParkingUsesWithdrawalDetail.details) {
                    defaultWithdrawalArea = bikeParkingUsesWithdrawalDetail.details.withdrawalArea
                    defaultWithdrawalDate = bikeParkingUsesWithdrawalDetail.details.withdrawalDate
                    defaultBikeCarryingOutDate = bikeParkingUsesWithdrawalDetail.details.bikeCarryingOutDate
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            withdrawalArea: defaultWithdrawalArea,
            withdrawalDate: defaultWithdrawalDate,
            bikeCarryingOutDate: defaultBikeCarryingOutDate,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutBikeParkingUseWithdrawalApplyRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutBikeParkingUseWithdrawalApplyResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyBikeParkingUseWithdrawalApply.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentBikeParkingUseWithdrawalApplyLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        withdrawalArea: request.withdrawalArea,
                        withdrawalDate: request.withdrawalDate,
                        bikeCarryingOutDate: request.bikeCarryingOutDate,
                    },
                    applyId: this.props.applyId,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUsesWithdrawal/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/bikeParkingUsesWithdrawal/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    バイク置場使用契約解約届
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    バイク置場の使用を中止したい場合に提出が必要です。使用規則で定められた期限までに必ず提出してください。
                </Box>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentBikeParkingUseWithdrawalApplyForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentBikeParkingUseWithdrawalApplyInputPage)
